import ClockIcon from '@src/svgs/ClockIcon';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import styled from 'styled-components';

import type { SpeakerStoryRef } from '@src/repositories/SpeakerRepository';
import type { FC } from 'react';

type Props = {
  story: SpeakerStoryRef;
  isSelected: boolean;
  onSelect: (storyId: string) => void;
  onOpen: (storyId: string) => void;
};

/** A single story a speaker appears in, shown under an expanded speaker row.
 *  Mirrors the Story Manager's clip rows (thumbnail + title + duration) so it
 *  reads as a story card, not a bare list item. Single-click highlights;
 *  double-click opens the story in Content Studio. */
const SpeakerStoryRow: FC<Props> = ({
  story,
  isSelected,
  onSelect,
  onOpen,
}) => {
  const duration =
    story.durationSeconds != null
      ? convertTimeToMMSS(story.durationSeconds)
      : '';
  return (
    <Row
      $selected={isSelected}
      onClick={event => {
        event.stopPropagation();
        onSelect(story.id);
      }}
      onDoubleClick={event => {
        event.stopPropagation();
        onOpen(story.id);
      }}
      title="Double-click to open"
      data-testid={`speaker-story-row-${story.id}`}
    >
      <Left>
        <Thumb>
          {story.thumbnailUrl ? (
            <ThumbImg
              src={story.thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : (
            <ThumbFallback />
          )}
        </Thumb>
        <Title>{story.title || 'Untitled story'}</Title>
      </Left>
      <Duration title="Duration">
        {duration && (
          <>
            <ClockIcon />
            <span>{duration}</span>
          </>
        )}
      </Duration>
    </Row>
  );
};

export default SpeakerStoryRow;

// Indent so the thumbnail sits under the parent speaker's avatar edge.
const STORY_INDENT = '48px';

const Row = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: 7px var(--spacing-md) 7px ${STORY_INDENT};
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? 'var(--surface-elevated)' : 'transparent'};
  transition: background 120ms ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
  flex: 1;
`;

const Thumb = styled.div`
  width: 72px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--border);
  flex-shrink: 0;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--border) 0%, var(--surface) 100%);
`;

const Title = styled.span`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Duration = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: var(--text-xs);
  color: var(--text-muted);

  svg {
    color: var(--text-muted);
  }
`;
